import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { PagingComponent } from '../../shared/paging/paging.component';
import { Video } from '../../models/video';
import { Project } from '../../models/project';
import { AuthService } from '../../services/auth/auth.service';
import { Table } from '../../models/table';
import { SearchBarComponent } from '../../shared/search-bar/search-bar.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FiltersModalComponent } from '../../shared/filters-modal/filters-modal.component';
import { SearchBarService } from '../../services/search-bar/search-bar.service';
import { UploadVideoComponent } from '../../shared/upload-video/upload-video.component';
import { isNullOrUndefined, isValidString } from '@pineapplelab/util';
import { MediaLibraryService } from '../../services/media-library/media-library.service';
import { firstValueFrom } from 'rxjs';
import { SortByEnum } from '../../enums/sortEnum.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PermissionsEnum } from '../../enums/permissions.enum';
import { IFilter } from '../../models/filter';
import { PagingQueryOptions } from '../../models/pagingQueryOptions ';
import { RoleEnum } from '../../enums/role.enum';
import { SortByComponent } from '../../shared/sort-by/sort-by.component';
import { MaterialModule } from '../../material/material.module';
import { ToasterService } from '../../services/toaster/toaster.service';
import { TableComponent } from '../../shared/table/table.component';
import { environment } from '../../../environments/environment';
import { MediaLibraryFile } from '../../enums/mediaLibraryFileType.enum';
@Component({
	selector: 'app-media-library',
	standalone: true,
	imports: [
		SharedModule,
		SearchBarComponent,
		FiltersModalComponent,
		MatProgressSpinnerModule,
		SortByComponent,
		MaterialModule
	],
	providers: [SearchBarService],
	templateUrl: './media-library.component.html',
	styleUrl: './media-library.component.scss'
})
export class MediaLibraryComponent implements OnInit, OnDestroy {

	@ViewChild('videoThumbnail', { static: true }) videoThumbnail: TemplateRef<any>;
	@ViewChild('videoDuration', { static: true }) videoDuration: TemplateRef<any>;
	@ViewChild('videoCreated', { static: true }) videoCreated: TemplateRef<any>;
	@ViewChild('videoLanguage', { static: true }) videoLanguage: TemplateRef<any>;
	@ViewChild('mediaLibraryActionColumn', { static: true }) mediaLibraryActionColumn: TemplateRef<any>;
	@ViewChild(PagingComponent) pagingComponent: PagingComponent;
	@ViewChild(TableComponent) tableComponent: TableComponent;

	modelVideo: Video = new Video();
	project: Project = new Project();
	table: Table<any>;
	data: Video[] = [];
	languageFilter: IFilter;
	searchString: string = '';
	showSpinner: boolean = true;
	searchBarService = inject(SearchBarService);
	sortingValue: SortByEnum = SortByEnum.RECENTLY_CREATED;
	PermissionsEnum = PermissionsEnum;
	searchBy: string = 'name_lowercase';
	videoUploadProgressQueue = this.mediaLibraryService.getVideosQueueProgress();

	queryOptions: PagingQueryOptions = {
		path: `${Project.PATH}/${this.authService.projectId}/${Video.PATH}`,
		pageSize: 8,
		filters: [],
		orderBy: 'created_at',
		orderDirection: 'desc',
	}

	sortByOptions: { value: SortByEnum, label: string }[] = [
		{ value: SortByEnum.ALPHABETICAL, label: 'general.alphabetical' },
		{ value: SortByEnum.RECENTLY_CREATED, label: 'general.recently_uploaded' },
	]
	pdfThumbnail: string = environment.pdfPlaceholderImage;
	MediaFileType = MediaLibraryFile;

	constructor(
		@Inject(MAT_DIALOG_DATA) public lang: any,
		private matDialogRef: MatDialogRef<MediaLibraryComponent>,
		private authService: AuthService,
		private dialog: MatDialog,
		private mediaLibraryService: MediaLibraryService
	) { }

	async ngOnInit(): Promise<void> {
		this.queryOptions.filters = this.getQueryFilters(this.authService.auth.user?.role);

		this.table = new Table({
			columns: [
				{ label: 'general.file_name', field: 'videoThumbnail', template: this.videoThumbnail, showOnlyTemplate: true },
				{ label: 'general.language', field: 'language', template: this.videoLanguage, showOnlyTemplate: true },
				{ label: 'general.duration', field: 'duration', template: this.videoDuration, showOnlyTemplate: true },
				{ label: 'general.uploaded_date', field: 'created_at', template: this.videoCreated, showOnlyTemplate: true },
			],
			selectable: true,
			actions: false,
			type: 'none'
		});

		this.mediaLibraryService.onQueueVideoUploadedCb = (video) => this.onVideoProcessed(video);
		this.mediaLibraryService.onQueueRefill = async () => await this.addProgressColumn();
		this.mediaLibraryService.onQueueEmptyCb = () => this.removeProgressColumn();
	}

	ngOnDestroy(): void {
		this.mediaLibraryService.onQueueVideoUploadedCb = null;
		this.mediaLibraryService.onQueueRefill = null;
		this.mediaLibraryService.onQueueEmptyCb = null;
	}

	async addProgressColumn(): Promise<void> {
		this.table.columns.push({ label: '', field: '__progress', template: this.mediaLibraryActionColumn, showOnlyTemplate: true });
		if (!this.data?.length) {
			await this.pagingComponent.loadFirstPage();
		} else {
			this.tableComponent.initializeTable();
		}
	}

	removeProgressColumn(): void {
		this.table.columns = this.table.columns.filter(t => t?.field !== '__progress');
		this.tableComponent.initializeTable();
	}

	getQueryFilters(role: RoleEnum): IFilter[] {
		let filters: IFilter[] = [
			// TODO: check how to fix this later
			// { isArray: false, property: 'uploaded', value: true }
		];

		if (!isNullOrUndefined(this.lang?.translation)) {
			filters.push({ property: 'language', value: this.lang.translation, isArray: false });
		}
		if (isNullOrUndefined(role)) {
			return filters;
		}
		if (role === RoleEnum.ADMIN) {
			filters.push({ property: 'created_by_admin', value: true, isArray: false });
		} else {
			filters.push({ property: 'created_by', value: this.authService.auth?.user?.id, isArray: false })
		}

		return filters;
	}

	filteredData(ev: any) {
		this.data = ev.map((video: any) => new Video(video));
		this.showSpinner = false;
	}

	openFilter() {
		this.dialog.open(FiltersModalComponent, {
			width: '632px', height: '280px'
		});
	}
	async uploadVideo() {
		const dialog = this.dialog.open(UploadVideoComponent, { width: '1100px', maxHeight: '680px', data: { uploadOnCourse: true, lang: this.lang }, disableClose: true });

		const dialogData = await firstValueFrom(dialog.afterClosed());
		if (dialogData) {
			setTimeout(async () => {
				await this.pagingComponent.loadFirstPage();
			}, 1000);
		}
	}

	close() {
		this.matDialogRef.close();
	}
	sendVideoData(video: Video) {
		if (!video?.uploaded || !isValidString(video?.video)) {
			return;
		}
		this.matDialogRef.close(video);
	}
	async deleteItem(item: any) {
		item.isDeleted = true;
		item.updatedBy = this.authService.auth.user.id;
		await this.mediaLibraryService.delete(this.authService.projectId, item);
	}
	async handleFilteredItems(searchString: string) {
		this.updateQueryOptions({ searchText: searchString });
		this.updateOrderByBasedOnSearchBy(this.searchBy);
		this.updateOrderByBasedOnSearch()
	}

	private updateOrderByBasedOnSearchBy(searchBy: string): void {
		this.searchBy = searchBy;
		this.updateQueryOptions({ orderBy: searchBy });
	}

	handleSearchBySelected(ev: string): void {
		this.searchBy = ev;
		this.updateOrderByBasedOnSearchBy(ev);
	}

	private updateOrderByBasedOnSearch(): void {
		if (this.queryOptions.searchText.length) {
			this.sortingValue = SortByEnum.ALPHABETICAL;
			this.updateOrderByBasedOnSearchBy(this.searchBy);
		}
	}

	handleSortOptionSelected(option: SortByEnum): void {
		this.sortingValue = option;
		this.updateOrderByBasedOnSort(option);
	}

	updateOrderByBasedOnSort(option: SortByEnum) {
		switch (option) {
			case SortByEnum.RECENTLY_CREATED:
				this.updateQueryOptions({ orderBy: 'created_at', orderDirection: 'desc', refreshPage: true });
				break;
			case SortByEnum.ALPHABETICAL:
				this.updateQueryOptions({ orderBy: 'name_lowercase', orderDirection: 'asc', refreshPage: true });
				break;
			default:
				break;
		}
	}

	private updateQueryOptions(newOptions: Partial<typeof this.queryOptions>): void {
		this.queryOptions = {
			...this.queryOptions,
			...newOptions
		};
	}

	onVideoProcessed(video: Video): void {
		const videoToReplaceIndex = this.data.findIndex(item => item?.id === video?.id);
		if (videoToReplaceIndex > -1) {
			this.data[videoToReplaceIndex].uploaded = true;
			this.data[videoToReplaceIndex].video = video?.video;
		}
	}
}
